import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.cam';

export interface BlogArticleMeta {
  title: string;
  description: string;
  slug: string;
  datePublished: string;
  dateModified: string;
  readTime: string;
  category: string;
  heroImage: string;
  heroImageAlt: string;
  keywords: string;
}

interface BlogLayoutProps {
  article: BlogArticleMeta;
  faq?: Array<{ question: string; answer: string }>;
  relatedArticles?: Array<{ title: string; slug: string }>;
  children: ReactNode;
}

export default function BlogLayout({ article, faq, relatedArticles, children }: BlogLayoutProps) {
  const { t } = useTranslation();

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.heroImage,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: { '@type': 'Organization', name: 'Kakobuy Polska', url: SITE_URL + '/' },
    publisher: { '@type': 'Organization', name: 'Kakobuy Polska', url: SITE_URL + '/', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png' } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': SITE_URL + article.slug },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('breadcrumb_home'),
        item: SITE_URL + '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: SITE_URL + article.slug,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: SITE_URL + article.slug,
      },
    ],
  };

  const faqSchema = faq && faq.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}

      <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 pt-24 md:pt-32 pb-16 md:pb-24">
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">{t('breadcrumb_home')}</Link>
            </li>
            <li><i className="ri-arrow-right-s-line text-xs" /></li>
            <li className="text-foreground-400">{article.category}</li>
            <li><i className="ri-arrow-right-s-line text-xs" /></li>
            <li className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</li>
          </ol>
        </nav>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase text-primary-700 bg-primary-100 rounded-full mb-4">
            {article.category}
          </span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed mb-6">
            {article.description}
          </p>
          <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.datePublished}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {article.readTime} {t('article_read_time')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {t('article_author')}
            </span>
          </div>
        </header>

        <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-12 bg-background-200">
          <img
            src={article.heroImage}
            alt={article.heroImageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="prose-custom">
          {children}
        </div>

        {faq && faq.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-8">
              Często zadawane pytania (FAQ)
            </h2>
            <div className="space-y-4">
              {faq.map((item, i) => (
                <details key={i} className="group bg-background-100 rounded-xl border border-background-200/50 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-200/50 transition-colors">
                    <span className="text-sm md:text-base font-semibold text-foreground-900 pr-8">
                      {item.question}
                    </span>
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:rotate-45 transition-transform shrink-0">
                      <i className="ri-add-line" />
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {relatedArticles && relatedArticles.length > 0 && (
          <section className="mt-16 pt-12 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
              {t('article_related')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={ra.slug}
                  className="p-4 bg-background-100 rounded-xl border border-background-200/50 hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
                >
                  <span className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors leading-snug">
                    {ra.title}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-16 pt-12 border-t border-background-200/70">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-4 text-base font-semibold bg-primary-500 text-background-50 rounded-xl hover:bg-primary-600 transition-all whitespace-nowrap shadow-lg shadow-primary-500/20"
            >
              {t('article_cta_spreadsheet')}
            </a>
            <Link
              to="/"
              className="px-8 py-4 text-base font-semibold bg-background-100 text-foreground-900 rounded-xl hover:bg-background-200 transition-all whitespace-nowrap border border-background-200/50"
            >
              {t('breadcrumb_home')}
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}